// Roofing Industries EZI-FLO rainwater presentation catalogue.
// This is visual-selection metadata only: final capacity, fall, outlets and
// project suitability remain designer/installer decisions outside RoofViz.

export type GutterId =
  | "quarter_round"
  | "half_round_swaged_168"
  | "old_gothic_150"
  | "old_gothic_125"
  | "j_line"
  | "half_round_160"
  | "half_round_flat_back"
  | "half_round_250"
  | "half_round_200"
  | "half_round_150"
  | "box_300"
  | "box_175"
  | "box_125";

export type GutterShape =
  | "quarter_round"
  | "half_round"
  | "half_round_swaged"
  | "half_round_flat_back"
  | "old_gothic"
  | "j_line"
  | "box";

export interface GutterSpec {
  id: GutterId;
  name: string;
  family: string;
  catalogueTier: "featured" | "extended";
  character: string;
  shape: GutterShape;
  /** Published nominal or drawing width used for the presentation mesh. */
  widthM: number;
  /** Published drawing depth, or a labelled proportional presentation where no depth is published. */
  depthM: number;
  fullCrossSectionMm2?: number;
  bracket: "concealed" | "external" | "concealed_or_external" | "heavy_external";
  geometryBasis: string;
  sourceUrl: string;
  sourceLabel: string;
  availabilityNote?: string;
}

export interface GutterCrossSectionPoint {
  outwardM: number;
  verticalM: number;
}

const RANGE_URL = "https://www.roof.co.nz/rainwater-categories/guttering-spouting";

export const GUTTERS: GutterSpec[] = [
  {
    id: "quarter_round",
    name: "Quarter Round",
    family: "Classic residential",
    catalogueTier: "featured",
    character: "A simple curved face that sits comfortably on most homes.",
    shape: "quarter_round",
    widthM: 0.15,
    depthM: 0.09,
    fullCrossSectionMm2: 6815,
    bracket: "concealed_or_external",
    geometryBasis: "RI published section and 6,815 mm² full cross-section.",
    sourceUrl: "https://www.roof.co.nz/rainwater-solutions/quarter-round",
    sourceLabel: "Roofing Industries EZI-FLO Quarter Round literature",
  },
  {
    id: "half_round_swaged_168",
    name: "168 Half Round Swaged",
    family: "Detailed curved face",
    catalogueTier: "extended",
    character: "A broad half-round form with twin front swages and visible matching brackets.",
    shape: "half_round_swaged",
    widthM: 0.168,
    depthM: 0.105,
    fullCrossSectionMm2: 7930,
    bracket: "external",
    geometryBasis: "RI published 168 × 105 mm presentation and 7,930 mm² full section.",
    sourceUrl: RANGE_URL,
    sourceLabel: "Roofing Industries EZI-FLO 1/2 Round Swaged literature",
  },
  {
    id: "old_gothic_150",
    name: "150 Old Gothic",
    family: "Colonial character",
    catalogueTier: "extended",
    character: "A larger traditional ogee profile with a pronounced decorative front.",
    shape: "old_gothic",
    widthM: 0.15,
    depthM: 0.095,
    bracket: "concealed_or_external",
    geometryBasis: "Proportional presentation of RI's published 150 Old Gothic silhouette; capacity is not published on the range page.",
    sourceUrl: RANGE_URL,
    sourceLabel: "Roofing Industries EZI-FLO 150 Old Gothic listing",
  },
  {
    id: "old_gothic_125",
    name: "125 Old Gothic",
    family: "Colonial character",
    catalogueTier: "extended",
    character: "A compact traditional ogee profile suited to heritage and character roof edges.",
    shape: "old_gothic",
    widthM: 0.125,
    depthM: 0.082,
    fullCrossSectionMm2: 6000,
    bracket: "concealed_or_external",
    geometryBasis: "RI published 125 family and 6,000 mm² full cross-section.",
    sourceUrl: RANGE_URL,
    sourceLabel: "Roofing Industries EZI-FLO 125 Old Gothic literature",
  },
  {
    id: "j_line",
    name: "J Line",
    family: "Clean sheet concealment",
    catalogueTier: "featured",
    character: "An extended sloping face hides the roof-sheet ends for a particularly clean edge.",
    shape: "j_line",
    widthM: 0.145,
    depthM: 0.092,
    fullCrossSectionMm2: 6240,
    bracket: "concealed",
    geometryBasis: "RI published J Line silhouette and 6,240 mm² full cross-section.",
    sourceUrl: RANGE_URL,
    sourceLabel: "Roofing Industries EZI-FLO J Line literature",
  },
  {
    id: "half_round_160",
    name: "160 Half Round",
    family: "Clean curved statement",
    catalogueTier: "featured",
    character: "A full curved face with generous water capacity and a clear contemporary outline.",
    shape: "half_round",
    widthM: 0.16,
    depthM: 0.1,
    fullCrossSectionMm2: 7930,
    bracket: "concealed_or_external",
    geometryBasis: "RI published 160 mm nominal size and 7,930 mm² full cross-section.",
    sourceUrl: RANGE_URL,
    sourceLabel: "Roofing Industries EZI-FLO 160 Half Round listing",
  },
  {
    id: "half_round_flat_back",
    name: "160 Half Round Flat Back",
    family: "Crisp curved edge",
    catalogueTier: "featured",
    character: "A generous round front, flat back and reinforcing grooves create a crisp modern edge.",
    shape: "half_round_flat_back",
    widthM: 0.153,
    depthM: 0.105,
    fullCrossSectionMm2: 8050,
    bracket: "concealed_or_external",
    geometryBasis: "RI 160 product name with published 153 × 105 mm section drawing and 8,050 mm² full section.",
    sourceUrl: "https://www.roof.co.nz/rainwater-solutions/160-half-round-flat-back",
    sourceLabel: "Roofing Industries EZI-FLO 160 Half Round Flat Back detail",
  },
  {
    id: "half_round_250",
    name: "EZI Flo 250 Half Round",
    family: "Large-capacity curved",
    catalogueTier: "extended",
    character: "An extra-large smooth half-round edge for projects requiring a substantial rainwater system.",
    shape: "half_round",
    widthM: 0.25,
    depthM: 0.156,
    fullCrossSectionMm2: 24540,
    bracket: "external",
    geometryBasis: "RI published 250 mm nominal size and 24,540 mm² section; depth is a proportional visual presentation.",
    sourceUrl: RANGE_URL,
    sourceLabel: "Roofing Industries EZI Flo 250 Half Round listing",
    availabilityNote: "Final use and capacity require project-specific RI drainage design.",
  },
  {
    id: "half_round_200",
    name: "EZI Flo 200 Half Round",
    family: "Large-capacity curved",
    catalogueTier: "extended",
    character: "A large smooth curved system with a strong, clean roof-edge presence.",
    shape: "half_round",
    widthM: 0.2,
    depthM: 0.125,
    fullCrossSectionMm2: 15706,
    bracket: "external",
    geometryBasis: "RI published 200 mm nominal size and 15,706 mm² section; depth is a proportional visual presentation.",
    sourceUrl: RANGE_URL,
    sourceLabel: "Roofing Industries EZI Flo 200 Half Round listing",
    availabilityNote: "Final use and capacity require project-specific RI drainage design.",
  },
  {
    id: "half_round_150",
    name: "EZI Flo 150 Half Round",
    family: "Compact modern curve",
    catalogueTier: "extended",
    character: "A compact smooth half-round form with a clean contemporary appearance.",
    shape: "half_round",
    widthM: 0.15,
    depthM: 0.094,
    fullCrossSectionMm2: 8835,
    bracket: "external",
    geometryBasis: "RI published 150 mm nominal size and 8,835 mm² section; depth is a proportional visual presentation.",
    sourceUrl: RANGE_URL,
    sourceLabel: "Roofing Industries EZI Flo 150 Half Round listing",
  },
  {
    id: "box_300",
    name: "300 Box",
    family: "Heavy-duty box",
    catalogueTier: "extended",
    character: "A very large angular edge intended primarily for commercial and industrial drainage demands.",
    shape: "box",
    widthM: 0.3,
    depthM: 0.105,
    fullCrossSectionMm2: 29355,
    bracket: "heavy_external",
    geometryBasis: "RI published 300 mm family and 29,355 mm² full cross-section.",
    sourceUrl: RANGE_URL,
    sourceLabel: "Roofing Industries EZI-FLO 300 Box Gutter literature",
    availabilityNote: "Normally a commercial or industrial option; expose only when the roofing team supplies it.",
  },
  {
    id: "box_175",
    name: "175 Box",
    family: "High-flow box",
    catalogueTier: "extended",
    character: "A bold square-edged system with a substantial architectural face.",
    shape: "box",
    widthM: 0.175,
    depthM: 0.12,
    fullCrossSectionMm2: 21525,
    bracket: "concealed_or_external",
    geometryBasis: "RI published 175 mm family and 21,525 mm² full cross-section.",
    sourceUrl: RANGE_URL,
    sourceLabel: "Roofing Industries EZI-FLO 175 Box Gutter literature",
    availabilityNote: "Normally a high-flow commercial option; final use is confirmed by RI.",
  },
  {
    id: "box_125",
    name: "125 Box",
    family: "Bold architectural edge",
    catalogueTier: "featured",
    character: "A taller square face visually conceals more of the roof-sheet ends.",
    shape: "box",
    widthM: 0.145,
    depthM: 0.08,
    fullCrossSectionMm2: 10000,
    bracket: "concealed_or_external",
    geometryBasis: "RI published 125 family detail and 10,000 mm² full cross-section.",
    sourceUrl: "https://www.roof.co.nz/rainwater-solutions/125mm-box-gutter",
    sourceLabel: "Roofing Industries EZI-FLO 125 Box Gutter detail",
  },
];

export const DEFAULT_GUTTER_ID: GutterId = "half_round_flat_back";

export function findGutter(id: string | undefined): GutterSpec | undefined {
  return GUTTERS.find((gutter) => gutter.id === id);
}

function halfEllipse(widthM: number, depthM: number, topDropM: number, segments = 14): GutterCrossSectionPoint[] {
  return Array.from({ length: segments + 1 }, (_, index) => {
    const ratio = index / segments;
    return {
      outwardM: widthM * ratio,
      verticalM: -topDropM - Math.sin(ratio * Math.PI) * (depthM - topDropM),
    };
  });
}

function swagedHalfRound(gutter: GutterSpec): GutterCrossSectionPoint[] {
  return halfEllipse(gutter.widthM, gutter.depthM, 0.018, 16).map((point) => {
    const ratio = point.outwardM / gutter.widthM;
    const groove = ratio > 0.7 && ratio < 0.84
      ? Math.sin(((ratio - 0.7) / 0.14) * Math.PI * 2) * 0.004
      : 0;
    return { outwardM: point.outwardM, verticalM: point.verticalM + groove };
  });
}

/** Open thin-sheet section used by both the Three.js extrusion and UI diagram. */
export function gutterCrossSection(gutter: GutterSpec): GutterCrossSectionPoint[] {
  if (gutter.shape === "quarter_round") {
    const curvedFront = Array.from({ length: 13 }, (_, index) => {
      const angle = (index / 12) * (Math.PI / 2);
      return {
        outwardM: Math.sin(angle) * gutter.widthM,
        verticalM: -Math.cos(angle) * gutter.depthM,
      };
    });
    return [{ outwardM: 0, verticalM: 0 }, ...curvedFront, { outwardM: gutter.widthM, verticalM: 0.008 }];
  }
  if (gutter.shape === "half_round") {
    return [{ outwardM: 0, verticalM: 0 }, ...halfEllipse(gutter.widthM, gutter.depthM, 0.018), { outwardM: gutter.widthM, verticalM: 0 }];
  }
  if (gutter.shape === "half_round_swaged") {
    return [{ outwardM: 0, verticalM: 0 }, ...swagedHalfRound(gutter), { outwardM: gutter.widthM, verticalM: 0 }];
  }
  if (gutter.shape === "half_round_flat_back") {
    return [
      { outwardM: 0, verticalM: 0 },
      { outwardM: 0, verticalM: -0.041 },
      ...halfEllipse(gutter.widthM, gutter.depthM, 0.041),
      { outwardM: gutter.widthM, verticalM: -0.026 },
    ];
  }
  if (gutter.shape === "old_gothic") {
    return [
      { outwardM: 0, verticalM: 0 },
      { outwardM: 0, verticalM: -gutter.depthM * 0.72 },
      { outwardM: gutter.widthM * 0.18, verticalM: -gutter.depthM },
      { outwardM: gutter.widthM * 0.52, verticalM: -gutter.depthM * 0.94 },
      { outwardM: gutter.widthM * 0.76, verticalM: -gutter.depthM * 0.66 },
      { outwardM: gutter.widthM * 0.68, verticalM: -gutter.depthM * 0.38 },
      { outwardM: gutter.widthM * 0.9, verticalM: -gutter.depthM * 0.2 },
      { outwardM: gutter.widthM, verticalM: 0.006 },
    ];
  }
  if (gutter.shape === "j_line") {
    return [
      { outwardM: 0, verticalM: 0 },
      { outwardM: 0, verticalM: -gutter.depthM * 0.82 },
      { outwardM: gutter.widthM * 0.58, verticalM: -gutter.depthM },
      { outwardM: gutter.widthM * 0.92, verticalM: -gutter.depthM * 0.32 },
      { outwardM: gutter.widthM, verticalM: 0.025 },
    ];
  }
  const baseWidth = Math.min(gutter.widthM * 0.84, gutter.widthM - 0.012);
  return [
    { outwardM: 0, verticalM: 0 },
    { outwardM: 0, verticalM: -gutter.depthM },
    { outwardM: baseWidth, verticalM: -gutter.depthM },
    { outwardM: gutter.widthM, verticalM: -0.012 },
    { outwardM: gutter.widthM, verticalM: 0 },
  ];
}
